import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';
import { MemberDetails } from '~/models/MemberDetails';
import { HStack } from '~/components/gluestack/hstack';
import { useAuth } from '~/components/providers/AuthProvider';
import { useShare } from '~/hooks/useShare';
import { useToastMessages } from '~/hooks/useToastMessages';

import { ButtonWrapper } from '~/components/ui/ButtonWrapper';
import { ShareModal } from '~/components/ui/ShareModal';
import { FollowButton } from '~/components/domain/common/FollowButton';
import { MembersService } from '~/services/domain/MembersService';
import { ContextSheet } from '~/components/ui/ContextSheet';
import { ComposeMessage } from '~/components/domain/messages/ComposeMessage';
import {
  Message01Icon,
  Share05Icon,
  UserEdit01Icon,
} from '@hugeicons-pro/core-stroke-rounded';

interface ProfileActionsProps {
  member: MemberDetails;
  className?: string;
}

export function ProfileActions({ member, className }: ProfileActionsProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const { accountStatus } = useAuth();
  const { handleShare, handleCopyUrl, handleOpenUrl, showShareModal, setShowShareModal, shareUrl } =
    useShare();
  const { showSuccessToast, showErrorToast } = useToastMessages();

  const [showMessageSheet, setShowMessageSheet] = useState(false);
  const [isMessageLoading, setIsMessageLoading] = useState(false);

  const currentMemberId = accountStatus?.MemberId;
  const isSelf = member?.Id === currentMemberId;

  const handleMessage = async () => {
    try {
      setIsMessageLoading(true);

      // First, try to get the last message ID with this member
      const lastMessageResult = await MembersService.getLastMessageId(member.Id);

      // If we got a result, navigate to the existing conversation
      if (lastMessageResult?.Id) {
        router.push(`/message/${lastMessageResult.Id}`);
      }
    } catch (error: any) {
      // If we get a 404 or other error, show the message sheet to start a new conversation
      if (error?.response?.status === 404 || error?.status === 404) {
        setShowMessageSheet(true);
      } else {
        // For other errors, show error toast
        showErrorToast(t('messages.errors.failedToLoad'), error);
      }
    } finally {
      setIsMessageLoading(false);
    }
  };

  const handleMessageSent = (messageId: number) => {
    // Close the sheet and navigate to the conversation
    setShowMessageSheet(false);
    if (messageId) {
      router.push(`/message/${messageId}`);
    }
  };

  const handleShareProfile = () => {
    handleShare({
      type: 'profile',
      userName: member.UserName,
    });
  };

  const handleEditProfile = () => {
    router.push('/account/profile');
  };

  // Don't render anything for self profile or admin users
  if (member.IsAdmin) {
    return null;
  }

  return (
    <>
      <HStack className={`flex-row flex-wrap justify-start gap-3 ${className || ''}`}>
        {!isSelf ? (
          <>
            <FollowButton member={member} size="sm" />
            <ButtonWrapper
              icon={Message01Icon}
              text={t('common.message')}
              size="sm"
              action="primary"
              variant="solid"
              onPress={handleMessage}
              isDisabled={isMessageLoading}
            />
          </>
        ) : (
          <ButtonWrapper
            icon={UserEdit01Icon}
            text={t('userMenu.editProfile')}
            size="sm"
            action="primary"
            variant="solid"
            onPress={handleEditProfile}
          />
        )}
        <ButtonWrapper
          icon={Share05Icon}
          size="sm"
          action="primary"
          variant="solid"
          onPress={handleShareProfile}
        />
      </HStack>

      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        shareUrl={shareUrl}
        onCopyUrl={handleCopyUrl}
        onOpenUrl={handleOpenUrl}
        title={t('profile.share.shareProfile')}
      />

      <ContextSheet
        isOpen={showMessageSheet}
        onClose={() => setShowMessageSheet(false)}
        title={t('messages.compose.messageToUser', { userName: member.UserName })}
        contentComponent={ComposeMessage}
        payload={{
          member,
          onMessageSent: handleMessageSent,
        }}
      />
    </>
  );
}
